import { useState, useRef } from 'react';
import { Button } from '@/components/ui/Button';
import { QariFormField } from '@/components/qari/QariFormField';
import {
  useQariDocuments,
  useQariMutations,
} from '@/hooks/useQariProfile';
import {
  QARI_DOCUMENT_KIND_LABELS,
  type QariDocument,
  type QariDocumentKind,
} from '@/types/qari';
import { signQariDocumentUrl } from '@/lib/queries/qari';
import { cn } from '@/lib/utils/cn';

type Props = {
  disabled?: boolean;
};

const KINDS: QariDocumentKind[] = [
  'ijazah',
  'identity',
  'certificate',
  'other',
];

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function StepDocuments({ disabled }: Props) {
  const docs = useQariDocuments();
  const { uploadDocument, deleteDocument } = useQariMutations();

  const [kind, setKind] = useState<QariDocumentKind>('ijazah');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function handleFile(file: File | null) {
    setError(null);
    if (!file) return;
    try {
      await uploadDocument.mutateAsync({ kind, file });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed.');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function preview(doc: QariDocument) {
    try {
      const url = await signQariDocumentUrl(doc.storage_path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setError('Could not generate preview link. Try again.');
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <QariFormField
          label="Document type"
          hint="Pick what kind of document you're about to upload."
        >
          <div className="flex flex-wrap gap-2">
            {KINDS.map((k) => {
              const active = kind === k;
              return (
                <button
                  key={k}
                  type="button"
                  disabled={disabled}
                  onClick={() => setKind(k)}
                  className={cn(
                    'px-3 h-8 rounded-full text-xs font-medium transition-colors',
                    active
                      ? 'bg-accent text-ink-50'
                      : 'bg-ink-100 dark:bg-ink-800 text-ink-700 dark:text-ink-200 hover:bg-ink-200 dark:hover:bg-ink-700',
                  )}
                >
                  {QARI_DOCUMENT_KIND_LABELS[k]}
                </button>
              );
            })}
          </div>
        </QariFormField>

        <div className="mt-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp"
            disabled={disabled || uploadDocument.isPending}
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            className="text-sm text-ink-700 dark:text-ink-200 file:mr-3 file:rounded-lg file:border-0 file:bg-ink-100 dark:file:bg-ink-800 file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-ink-200 dark:hover:file:bg-ink-700"
          />
          <p className="text-xs text-ink-500 dark:text-ink-400 mt-1.5">
            PDF, JPG, PNG, or WebP. Max 10 MB.
            {uploadDocument.isPending && (
              <span className="ml-2 text-accent-600 dark:text-accent-400">
                Uploading…
              </span>
            )}
          </p>
          {error && (
            <p
              className="text-xs text-rose-600 dark:text-rose-400 mt-1.5"
              role="alert"
            >
              {error}
            </p>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-[0.18em] text-ink-500 dark:text-ink-400 mb-3">
          Uploaded documents
        </h3>
        {docs.isLoading && (
          <p className="text-sm text-ink-500 dark:text-ink-400">Loading…</p>
        )}
        {docs.data && docs.data.length === 0 && (
          <p className="text-sm text-ink-500 dark:text-ink-400 italic">
            Nothing uploaded yet.
          </p>
        )}
        {docs.data && docs.data.length > 0 && (
          <ul className="divide-y divide-ink-200/60 dark:divide-ink-800/60 rounded-lg border border-ink-200/60 dark:border-ink-800/60">
            {docs.data.map((doc) => (
              <li
                key={doc.id}
                className="flex items-center gap-3 px-3 py-2.5"
              >
                <span className="text-xs uppercase tracking-wider text-ink-500 dark:text-ink-400 w-20 shrink-0">
                  {QARI_DOCUMENT_KIND_LABELS[doc.kind]}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-ink-900 dark:text-ink-50 truncate">
                    {doc.original_filename}
                  </p>
                  <p className="text-xs text-ink-500 dark:text-ink-400">
                    {formatBytes(doc.size_bytes)} ·{' '}
                    {new Date(doc.created_at).toLocaleDateString()}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => preview(doc)}
                  disabled={disabled}
                >
                  View
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteDocument.mutate(doc)}
                  disabled={disabled || deleteDocument.isPending}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
